import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import StatusBadge from "./StatusBadge";
import { renderField } from "../../utils/tableUtils";
import { createPortal } from "react-dom";
import { clearRecordDetail } from "../../store/slices/modalSlice";

const DetailModal = () => {
  const dispatch = useDispatch();
  const recordDetail = useSelector((state) => state.modal.modals.recordDetail);
  if (!recordDetail?.open || !recordDetail.data) return null;

  const record = recordDetail.data;

  // Stream recordings through the authenticated API proxy. This fixes
  // CallGrid playback (redirects / CORS / content-type) and works for Ringba
  // too. Falls back to the raw URL when we don't have a record id.
  const recordingProxyUrl = (() => {
    if (!record?.recordingUrl) return null;
    if (!record?._id) return record.recordingUrl;
    const base = process.env.REACT_APP_API_BASE || "";
    let token = null;
    try {
      token = localStorage.getItem("token");
    } catch (e) {
      token = null;
    }
    const qs = token ? `?token=${encodeURIComponent(token)}` : "";
    return `${base}/api/calls/recording/${record._id}${qs}`;
  })();

  const handleClose = () => {
    dispatch(clearRecordDetail());
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!record) return null;

  return createPortal(
    <>
      <div
        className="modal-backdrop show"
        style={{ zIndex: 1040 }}
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ zIndex: 1050 }}
        onClick={handleBackdropClick}
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                Call Record Details
                {record?.systemCallId && (
                  <small className="text-muted ms-2">
                    #{record.systemCallId}
                  </small>
                )}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="detail-section">
                    <h6>Basic Information</h6>
                    {renderField("Time", record.callTimestamp, "date")}
                    {renderField("Caller ID", record.callerId)}
                    {renderField("Campaign", record.campaignName)}
                    {renderField("Target", record.target_name)}
                    {renderField("Duration", `${record.durationSec} seconds`)}
                  </div>

                  <div className="detail-section">
                    <h6>QC Information</h6>
                    {record.qc?.disposition && (
                      <div className="detail-field">
                        <label>Status:</label>
                        <StatusBadge status={record.qc.disposition} size="lg" />
                      </div>
                    )}
                    {renderField("Sub Disposition", record.qc?.sub_disposition)}
                    {renderField("Reason", record.qc?.reason)}
                    {renderField("Summary", record.qc?.summary)}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="detail-section">
                    <h6>System Information</h6>
                    {renderField("System Call ID", record.systemCallId)}
                    {renderField(
                      "System Publisher ID",
                      record.systemPublisherId
                    )}
                    {renderField(
                      "Buyer",
                      record.systemBuyerName || record.systemBuyerId
                    )}
                  </div>

                  {record.recordingUrl && (
                    <div className="detail-section">
                      <h6>Recording</h6>
                      <audio controls preload="none" className="w-100" src={recordingProxyUrl}>
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}

                  {record.transcript && (
                    <div className="detail-section">
                      <h6 style={{ fontWeight: "bold" }}>Transcript</h6>
                      <div
                        className="transcript-box"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          padding: "8px",
                          borderRadius: "4px",
                        }}
                      >
                        {record.transcript}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleClose}>
                Close
              </button>
              {record.recordingUrl && (
                <a
                  href={recordingProxyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  <i className="bi bi-download me-1" />
                  Download Recording
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default React.memo(DetailModal);
